import inventoryText from "../inventoryI18n.js";
import React, { useEffect, useState } from "react";
import axios from "axios";
import Modal from "react-bootstrap/Modal";

const formatMoney = (value) => {
  const amount = Number(value);

  return `$${(
    Number.isFinite(amount) ? amount : 0
  ).toFixed(2)}`;
};

const formatDate = (value) => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleString();
};

const escapeHtml = (value) =>
  String(
    value === null || value === undefined
      ? ""
      : value
  ).replace(
    /[&<>"']/g,
    (character) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#039;",
      })[character]
  );

const authConfig = () => {
  const tokens = JSON.parse(
    localStorage.getItem("authTokens") || "{}"
  );

  return {
    headers: {
      Authorization:
        `Bearer ${tokens.access}`,
    },
  };
};

const errorMessage = (
  error,
  fallback
) => {
  const data =
    error.response && error.response.data;

  return data && data.error
    ? data.error
    : fallback;
};

const createIdempotencyKey = () => {
  if (
    window.crypto &&
    typeof window.crypto.randomUUID ===
      "function"
  ) {
    return window.crypto.randomUUID();
  }

  return (
    "xxxxxxxx-xxxx-4xxx-yxxx-" +
    "xxxxxxxxxxxx"
  ).replace(
    /[xy]/g,
    (character) => {
      const random =
        Math.floor(Math.random() * 16);
      const value =
        character === "x"
          ? random
          : (random & 0x3) | 0x8;

      return value.toString(16);
    }
  );
};

const printDocument = (
  title,
  contents
) => {
  const oldFrame =
    document.getElementById(
      "purchase-document-print-frame"
    );

  if (oldFrame) {
    oldFrame.remove();
  }

  const frame =
    document.createElement("iframe");

  frame.id =
    "purchase-document-print-frame";
  frame.setAttribute(
    "title",
    "Purchase document print frame"
  );

  frame.style.position = "fixed";
  frame.style.width = "1px";
  frame.style.height = "1px";
  frame.style.right = "0";
  frame.style.bottom = "0";
  frame.style.border = "0";
  frame.style.opacity = "0";
  frame.style.pointerEvents = "none";

  document.body.appendChild(frame);

  const printWindow = frame.contentWindow;

  if (!printWindow) {
    frame.remove();

    window.alert(
      "Unable to prepare the print preview."
    );

    return;
  }

  const printDocumentValue =
    printWindow.document;

  const cleanup = () => {
    window.setTimeout(() => {
      if (frame.parentNode) {
        frame.parentNode.removeChild(frame);
      }
    }, 250);
  };

  printDocumentValue.open();

  printDocumentValue.write(
    `<!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${escapeHtml(title)}</title>
          <style>
            @page {
              size: A4;
              margin: 13mm;
            }

            * {
              box-sizing: border-box;
            }

            body {
              margin: 0;
              color: #17212b;
              font: 12px Arial, sans-serif;
            }

            header {
              display: flex;
              justify-content: space-between;
              gap: 24px;
              padding-bottom: 14px;
              border-bottom: 3px solid #17384a;
            }

            .brand {
              color: #17384a;
              font-size: 22px;
              font-weight: 700;
            }

            .document-number {
              text-align: right;
              line-height: 1.5;
            }

            h1 {
              margin: 22px 0 16px;
              color: #17384a;
              text-align: center;
            }

            .meta {
              display: grid;
              grid-template-columns:
                repeat(2, minmax(0, 1fr));
              gap: 9px 24px;
              margin-bottom: 16px;
              padding: 13px;
              border: 1px solid #d7dee4;
              background: #f6f8fa;
            }

            table {
              width: 100%;
              border-collapse: collapse;
            }

            th,
            td {
              padding: 8px;
              border: 1px solid #d7dee4;
              text-align: left;
            }

            th {
              background: #17384a;
              color: #ffffff;
            }

            .number {
              text-align: right;
              white-space: nowrap;
            }

            .total td {
              background: #e7edf1;
              font-weight: 700;
            }

            .notes {
              margin-top: 14px;
              padding: 12px;
              border: 1px solid #d7dee4;
            }

            footer {
              display: flex;
              justify-content: space-between;
              margin-top: 18px;
              padding-top: 10px;
              border-top: 1px solid #d7dee4;
              color: #66727c;
            }
          </style>
        </head>

        <body>${contents}</body>
      </html>`
  );

  printDocumentValue.close();

  printWindow.addEventListener(
    "afterprint",
    cleanup,
    {
      once: true,
    }
  );

  window.setTimeout(() => {
    printWindow.focus();
    printWindow.print();
  }, 250);

  window.setTimeout(
    cleanup,
    60000
  );
};

const printDeliveryReceipt = (
  receipt
) => {
  const items = (
    receipt.items || []
  ).filter(
    (item) =>
      Number(
        item.quantity_received_now
      ) > 0
  );

  const total = items.reduce(
    (sum, item) =>
      sum +
      Number(item.line_total || 0),
    0
  );

  const rows = items.length
    ? items
        .map(
          (item) => `
            <tr>
              <td>
                ${escapeHtml(
                  item.product_name
                )}
              </td>

              <td class="number">
                ${escapeHtml(
                  item.quantity_received_now
                )}
              </td>

              <td class="number">
                ${escapeHtml(
                  formatMoney(
                    item.unit_price
                  )
                )}
              </td>

              <td class="number">
                ${escapeHtml(
                  formatMoney(
                    item.line_total
                  )
                )}
              </td>
            </tr>
          `
        )
        .join("")
    : `
        <tr>
          <td colspan="4">
            No units were received on this record.
          </td>
        </tr>
      `;

  const receiver =
    receipt.received_by || {};

  printDocument(
    `Delivery Receipt - ${
      receipt.receipt_number
    }`,
    `
      <header>
        <div class="brand">
          OMEGA SMART SYSTEMS
        </div>

        <div class="document-number">
          <strong>
            ${escapeHtml(
              receipt.receipt_number
            )}
          </strong>
          <br />

          ${escapeHtml(
            formatDate(
              receipt.creation_date
            )
          )}
        </div>
      </header>

      <h1>{inventoryText("inventory.DeliveryReceipt")}</h1>

      <section class="meta">
        <div>
          <strong>Order:</strong>
          ${escapeHtml(
            receipt.order_number
          )}
        </div>

        <div>
          <strong>Request:</strong>
          ${escapeHtml(
            receipt.request_number
          )}
        </div>

        <div>
          <strong>Supplier:</strong>
          ${escapeHtml(
            receipt.supplier &&
              receipt.supplier
                .supplier_name
          )}
        </div>

        <div>
          <strong>Warehouse:</strong>
          ${escapeHtml(
            receipt.warehouse &&
              receipt.warehouse
                .warehouse_name
          )}
        </div>

        <div>
          <strong>Received by:</strong>
          ${escapeHtml(
            receiver.full_name ||
              receiver.username ||
              "—"
          )}
        </div>

        <div>
          <strong>Reference:</strong>
          ${escapeHtml(
            receipt.reference || "—"
          )}
        </div>
      </section>

      <table>
        <thead>
          <tr>
            <th>{inventoryText("inventory.Product")}</th>
            <th>{inventoryText("inventory.Quantity")}</th>
            <th>Unit Price</th>
            <th>Total</th>
          </tr>
        </thead>

        <tbody>
          ${rows}

          <tr class="total">
            <td colspan="3">
              Receipt Total
            </td>

            <td class="number">
              ${escapeHtml(
                formatMoney(total)
              )}
            </td>
          </tr>
        </tbody>
      </table>

      ${
        receipt.notes
          ? `
              <div class="notes">
                <strong>{inventoryText("inventory.Notes")}:</strong>
                <br />

                ${escapeHtml(
                  receipt.notes
                )}
              </div>
            `
          : ""
      }

      <footer>
        <span>Omega Smart Systems</span>

        <span>
          ${escapeHtml(
            receipt.receipt_number
          )}
        </span>
      </footer>
    `
  );
};

const printRefundReceipt = (
  refund
) => {
  printDocument(
    `Refund Receipt - ${
      refund.refund_number
    }`,
    `
      <header>
        <div class="brand">
          OMEGA SMART SYSTEMS
        </div>

        <div class="document-number">
          <strong>
            ${escapeHtml(
              refund.refund_number
            )}
          </strong>
          <br />

          ${escapeHtml(
            formatDate(
              refund.creation_date
            )
          )}
        </div>
      </header>

      <h1>Purchase Refund Receipt</h1>

      <section class="meta">
        <div>
          <strong>Order:</strong>
          ${escapeHtml(
            refund.order_number
          )}
        </div>

        <div>
          <strong>
            Delivery Receipt:
          </strong>

          ${escapeHtml(
            refund.receipt_number
          )}
        </div>

        <div>
          <strong>Supplier:</strong>
          ${escapeHtml(
            refund.supplier &&
              refund.supplier
                .supplier_name
          )}
        </div>

        <div>
          <strong>Warehouse:</strong>
          ${escapeHtml(
            refund.warehouse &&
              refund.warehouse
                .warehouse_name
          )}
        </div>

        <div>
          <strong>Processed by:</strong>
          ${escapeHtml(
            refund.processed_by &&
              (
                refund.processed_by
                  .full_name ||
                refund.processed_by
                  .username
              )
          )}
        </div>

        <div>
          <strong>Request:</strong>
          ${escapeHtml(
            refund.request_number
          )}
        </div>
      </section>

      <table>
        <thead>
          <tr>
            <th>{inventoryText("inventory.Product")}</th>
            <th>Refund Quantity</th>
            <th>Unit Price</th>
            <th>Refund Amount</th>
          </tr>
        </thead>

        <tbody>
          <tr>
            <td>
              ${escapeHtml(
                refund.product &&
                  refund.product
                    .product_name
              )}
            </td>

            <td class="number">
              ${escapeHtml(
                refund.quantity
              )}
            </td>

            <td class="number">
              ${escapeHtml(
                formatMoney(
                  refund.unit_price_usd
                )
              )}
            </td>

            <td class="number">
              ${escapeHtml(
                formatMoney(
                  refund.amount_usd
                )
              )}
            </td>
          </tr>

          <tr class="total">
            <td colspan="3">
              Refund Total
            </td>

            <td class="number">
              ${escapeHtml(
                formatMoney(
                  refund.amount_usd
                )
              )}
            </td>
          </tr>
        </tbody>
      </table>

      <div class="notes">
        <strong>Reason:</strong>
        <br />

        ${escapeHtml(
          refund.reason
        )}
      </div>

      <footer>
        <span>Omega Smart Systems</span>

        <span>
          ${escapeHtml(
            refund.refund_number
          )}
        </span>
      </footer>
    `
  );
};



const ReceiptCard = ({
  receipt,
  onPrintRefund,
}) => {
  const visibleItems = (
    receipt.items || []
  ).filter(
    (item) =>
      Number(
        item.quantity_received_now
      ) > 0
  );

  return (
    <article
      className="purchase-document-card"
    >
      <div
        className={
          "purchase-document-card-heading"
        }
      >
        <div>
          <strong>
            {receipt.receipt_number}
          </strong>

          <span>
            {formatDate(
              receipt.creation_date
            )}
          </span>
        </div>

        <button
          type="button"
          className={
            "purchase-button " +
            "purchase-button-primary"
          }
          onClick={() =>
            printDeliveryReceipt(receipt)
          }
        >{inventoryText("inventory.PrintReceipt")}</button>
      </div>

      <dl
        className={
          "purchase-document-facts"
        }
      >
        <div>
          <dt>{inventoryText("inventory.Supplier")}</dt>

          <dd>
            {(
              receipt.supplier &&
              receipt.supplier
                .supplier_name
            ) || "—"}
          </dd>
        </div>

        <div>
          <dt>{inventoryText("inventory.Warehouse")}</dt>

          <dd>
            {(
              receipt.warehouse &&
              receipt.warehouse
                .warehouse_name
            ) || "—"}
          </dd>
        </div>

        <div>
          <dt>Reference</dt>

          <dd>
            {receipt.reference || "—"}
          </dd>
        </div>
      </dl>

      <div
        className={
          "purchase-document-items"
        }
      >
        {visibleItems.length ? (
          visibleItems.map((item) => (
            <div
              key={
                item.goods_receipt_item_id
              }
            >
              <span>
                {item.product_name}
              </span>

              <strong>
                {
                  item.quantity_received_now
                }{" "}
                ×{" "}
                {formatMoney(
                  item.unit_price
                )}
              </strong>
            </div>
          ))
        ) : (
          <p>
            No units were received on
            this record.
          </p>
        )}
      </div>

      {receipt.notes && (
        <div
          className={
            "purchase-document-note"
          }
        >
          <strong>{inventoryText("inventory.Notes")}</strong>
          <p>{receipt.notes}</p>
        </div>
      )}

      {(
        receipt.refunds || []
      ).length > 0 && (
        <div
          className={
            "purchase-document-refunds"
          }
        >
          <strong>
            Refund Receipts
          </strong>

          {(
            receipt.refunds || []
          ).map((refund) => (
            <button
              type="button"
              className={
                "supplier-row-action"
              }
              key={
                refund.purchase_refund_id
              }
              onClick={() =>
                onPrintRefund(refund)
              }
            >
              {refund.refund_number}
              {" · "}
              {formatMoney(
                refund.amount_usd
              )}
            </button>
          ))}
        </div>
      )}
    </article>
  );
};

export function PurchaseDocumentModal({
  request,
  apiUrl,
  onClose,
}) {
  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState("");

  const [
    data,
    setData,
  ] = useState(null);

  useEffect(() => {
    if (!request) {
      setData(null);
      setError("");

      return undefined;
    }

    let active = true;

    setLoading(true);
    setError("");
    setData(null);

    const endpoint =
      request.kind === "order"
        ? (
            `${apiUrl}/inventory/` +
            "purchases/orders/" +
            `${request.id}/receipts/`
          )
        : request.kind === "delivery"
        ? (
            `${apiUrl}/inventory/` +
            "purchases/receipts/" +
            `${request.id}/`
          )
        : (
            `${apiUrl}/inventory/` +
            "purchases/refunds/" +
            `${request.id}/`
          );

    axios
      .get(
        endpoint,
        authConfig()
      )
      .then((response) => {
        if (active) {
          setData(response.data);
        }
      })
      .catch((requestError) => {
        if (active) {
          setError(
            errorMessage(
              requestError,
              "Unable to load this " +
                "purchase document."
            )
          );
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [
    request,
    apiUrl,
  ]);

  const receipts =
    data && request
      ? request.kind === "order"
        ? data.receipts || []
        : data.receipt
        ? [data.receipt]
        : []
      : [];

  const refund =
    data && data.refund;

  return (
    <Modal
      show={Boolean(request)}
      onHide={onClose}
      className={
        "purchase-modal " +
        "purchase-document-modal"
      }
      centered
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {request &&
          request.kind === "refund"
            ? "Refund Receipt"
            : "Delivery Receipts"}

          {request && request.label
            ? ` — ${request.label}`
            : ""}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div
            className={
              "purchase-inspection-state"
            }
          >
            Loading receipts…
          </div>
        ) : error ? (
          <div
            className={
              "purchase-form-error"
            }
            role="alert"
          >
            {error}
          </div>
        ) : refund ? (
          <article
            className={
              "purchase-document-card"
            }
          >
            <div
              className={
                "purchase-document-" +
                "card-heading"
              }
            >
              <div>
                <strong>
                  {refund.refund_number}
                </strong>

                <span>
                  {formatDate(
                    refund.creation_date
                  )}
                </span>
              </div>

              <button
                type="button"
                className={
                  "purchase-button " +
                  "purchase-button-primary"
                }
                onClick={() =>
                  printRefundReceipt(
                    refund
                  )
                }
              >
                Print Refund Receipt
              </button>
            </div>

            <dl
              className={
                "purchase-document-facts"
              }
            >
              <div>
                <dt>{inventoryText("inventory.Product")}</dt>

                <dd>
                  {
                    refund.product
                      .product_name
                  }
                </dd>
              </div>

              <div>
                <dt>{inventoryText("inventory.Quantity")}</dt>
                <dd>{refund.quantity}</dd>
              </div>

              <div>
                <dt>{inventoryText("inventory.Amount")}</dt>

                <dd>
                  {formatMoney(
                    refund.amount_usd
                  )}
                </dd>
              </div>
            </dl>

            <div
              className={
                "purchase-document-note"
              }
            >
              <strong>{inventoryText("inventory.Reason")}</strong>
              <p>{refund.reason}</p>
            </div>
          </article>
        ) : receipts.length ? (
          <div
            className={
              "purchase-document-list"
            }
          >
            {receipts.map(
              (receipt) => (
                <ReceiptCard
                  key={
                    receipt
                      .goods_receipt_id
                  }
                  receipt={receipt}
                  onPrintRefund={
                    printRefundReceipt
                  }
                />
              )
            )}
          </div>
        ) : (
          <div
            className={
              "purchase-inspection-state"
            }
          >
            No delivery receipts have
            been recorded.
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <button
          type="button"
          className={
            "purchase-button " +
            "purchase-button-secondary"
          }
          onClick={onClose}
        >{inventoryText("inventory.Close")}</button>
      </Modal.Footer>
    </Modal>
  );
}



export function PurchaseRefundPanel({
  item,
  apiUrl,
  onCancel,
  onCompleted,
}) {
  const maximum = Number(
    item.quantity_refundable || 0
  );

  const unitPrice = Number(
    item.unit_price_usd || 0
  );

  const [
    quantity,
    setQuantity,
  ] = useState("1");

  const [
    reason,
    setReason,
  ] = useState("");

  const [
    error,
    setError,
  ] = useState("");

  const [
    submitting,
    setSubmitting,
  ] = useState(false);

  const [
    idempotencyKey,
    setIdempotencyKey,
  ] = useState(
    createIdempotencyKey
  );

  const numericQuantity =
    Number(quantity);

  const amount =
    Number.isInteger(
      numericQuantity
    )
      ? numericQuantity * unitPrice
      : 0;

  useEffect(() => {
    setQuantity("1");
    setReason("");
    setError("");
    setSubmitting(false);

    setIdempotencyKey(
      createIdempotencyKey()
    );
  }, [
    item.goods_receipt_item_id,
  ]);

  const submitRefund = async (
    event
  ) => {
    event.preventDefault();
    setError("");

    if (
      !Number.isInteger(
        numericQuantity
      ) ||
      numericQuantity < 1 ||
      numericQuantity > maximum
    ) {
      setError(
        "Refund quantity must be " +
          `between 1 and ${maximum}.`
      );

      return;
    }

    if (!reason.trim()) {
      setError(
        "Enter the reason for this refund."
      );

      return;
    }

    setSubmitting(true);

    try {
      const response =
        await axios.post(
          `${apiUrl}/inventory/` +
            "purchases/receipt-items/" +
            `${
              item.goods_receipt_item_id
            }/refund/`,
          {
            idempotency_key:
              idempotencyKey,
            quantity:
              numericQuantity,
            reason:
              reason.trim(),
          },
          authConfig()
        );

      onCompleted(
        response.data.refund
      );
    } catch (requestError) {
      setError(
        errorMessage(
          requestError,
          "Unable to process " +
            "this refund."
        )
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      className={
        "purchase-refund-panel"
      }
      onSubmit={submitRefund}
    >
      <div
        className={
          "purchase-refund-panel-heading"
        }
      >
        <div>
          <strong>
            Refund delivered units
          </strong>

          <span>
            Available: {maximum}
            {" · "}
            Unit price:{" "}
            {formatMoney(unitPrice)}
          </span>
        </div>

        <strong>
          {formatMoney(amount)}
        </strong>
      </div>

      {error && (
        <div
          className={
            "purchase-form-error"
          }
          role="alert"
        >
          {error}
        </div>
      )}

      <div
        className={
          "purchase-refund-fields"
        }
      >
        <label>
          Refund Quantity

          <input
            type="number"
            min="1"
            max={maximum}
            step="1"
            value={quantity}
            disabled={submitting}
            onChange={(event) =>
              setQuantity(
                event.target.value
              )
            }
            required
          />
        </label>

        <label>
          Refund Reason

          <textarea
            rows="3"
            maxLength="5000"
            value={reason}
            disabled={submitting}
            onChange={(event) =>
              setReason(
                event.target.value
              )
            }
            placeholder="Required"
            required
          />
        </label>
      </div>

      <div
        className={
          "purchase-refund-actions"
        }
      >
        <button
          type="button"
          className={
            "purchase-button " +
            "purchase-button-secondary"
          }
          disabled={submitting}
          onClick={onCancel}
        >{inventoryText("inventory.Cancel")}</button>

        <button
          type="submit"
          className={
            "purchase-button " +
            "purchase-refund-confirm"
          }
          disabled={
            submitting ||
            maximum < 1
          }
        >
          {submitting
            ? "Refunding..."
            : "Confirm Refund"}
        </button>
      </div>
    </form>
  );
}
